import React, { useState, useEffect, useMemo } from 'react'
import {Card, Form, Button} from "react-bootstrap";
import CardBodyContentContainer from "../../../common/styles/CardBodyContentContainer";
import CardTitle from "../../../common/styles/CardTitle";
import BooksitoutIcon from "../../../config/BooksitoutIcon";
import Modal from "../../../common/Modal";
import {BooksitoutServer} from "../../../config/BooksitoutServer";
import {useParams} from "react-router-dom";

const LibraryMembershipDetailPasswordCard: React.FC<{passwordFromServer: string | null}> = ({passwordFromServer}) => {
    const {membershipId} = useParams()

    const [password, setPassword] = useState(passwordFromServer);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [newPassword, setNewPassword] = useState('');

    useEffect(() => {
        setPassword(passwordFromServer);
    }, [passwordFromServer]);

    // Generate random positions for dots
    const dotPositions = useMemo(() => {
        if (!password) return [];

        const positions: Array<{x: number; y: number; delay: number; size: number; opacity: number}> = [];
        const areaWidth = 200;
        const areaHeight = 60;

        // Create many more dots - base number plus extras for visual richness
        const totalDots = Math.max(password.length * 8, 200); // At least 40 dots, more for longer passwords

        for (let i = 0; i < totalDots; i++) {
            positions.push({
                x: Math.random() * (areaWidth - 20) + 10, // 10px margin from edges
                y: Math.random() * (areaHeight - 20) + 10,
                delay: Math.random() * 3, // Random delay between 0-3s
                size: Math.random() * 8 + 2, // Size between 2px and 10px
                opacity: Math.random() * 0.4 + 0.2 // Opacity between 0.2 and 0.6
            });
        }
        return positions;
    }, [password]);

    const handleMouseEnter = () => {
        if (password) {
            setIsPasswordVisible(true);

            if (timeoutId) {
                clearTimeout(timeoutId);
            }

            const newTimeoutId = setTimeout(() => {
                setIsPasswordVisible(false);
            }, 10000);

            setTimeoutId(newTimeoutId);
        }
    };

    const handleMouseLeave = () => {
        // Don't hide immediately on mouse leave, let the 10s timeout handle it
    };

    useEffect(() => {
        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, [timeoutId]);

    const handleSetPassword = () => {
        if (isPasswordVisible || !password) {
            setShowPasswordModal(true);
        }
    };

    const handlePasswordSubmit = (e) => {
        e.preventDefault();

        BooksitoutServer
            .put(`/v1/library/membership/${membershipId}`, {password: newPassword})
            .then(_ => {})

        setPassword(newPassword)
        setShowPasswordModal(false);
        setNewPassword('');
    };

    const renderPasswordDisplay = () => {
        if (!password) {
            return (
                <div
                    style={{
                        color: '#666',
                        fontStyle: 'italic',
                        cursor: 'pointer',
                        padding: '10px',
                        borderRadius: '8px',
                        transition: 'background-color 0.2s ease'
                    }}
                    onClick={handleSetPassword}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'var(--card-card-bg-color)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                >
                    비밀번호가 설정되지 않았어요
                </div>
            );
        }

        return (
            <div
                style={{
                    position: 'relative',
                    width: '200px',
                    height: '60px',
                    cursor: 'pointer'
                }}
                onClick={handleSetPassword}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
            >
                {/* Dots Layer */}
                <div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        opacity: isPasswordVisible ? 0 : 1,
                        transition: 'opacity 0.3s ease-in-out',
                        pointerEvents: isPasswordVisible ? 'none' : 'auto'
                    }}
                >
                    {dotPositions.map((position, index) => (
                        <span
                            key={index}
                            className="scattered-dot"
                            style={{
                                position: 'absolute',
                                left: `${position.x}px`,
                                top: `${position.y}px`,
                                width: `${position.size}px`,
                                height: `${position.size}px`,
                                borderRadius: '50%',
                                backgroundColor: 'var(--primary-color)',
                                animationDelay: `${position.delay}s`,
                                opacity: position.opacity,
                                transition: 'all 0.3s ease'
                            }}
                        />
                    ))}
                </div>

                {/* Password Layer */}
                <div
                    style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '1.5rem',
                        fontWeight: 'bold',
                        color: 'var(--primary-color)',
                        textShadow: '0 0 10px rgba(0, 123, 255, 0.5)',
                        letterSpacing: '2px',
                        opacity: isPasswordVisible ? 1 : 0,
                        transform: `scale(${isPasswordVisible ? 1 : 0.9})`,
                        transition: 'opacity 0.3s ease-in-out, transform 0.3s ease-in-out',
                        pointerEvents: isPasswordVisible ? 'auto' : 'none'
                    }}
                >
                    {password}
                </div>

                <style dangerouslySetInnerHTML={{
                    __html: `
                        .scattered-dot {
                            animation: subtleGlow 3s ease-in-out infinite;
                        }
                        @keyframes subtleGlow {
                            0% {
                                opacity: 0.4;
                                transform: scale(0.9);
                            }
                            50% {
                                opacity: 0.7;
                                transform: scale(1.05);
                            }
                            100% {
                                opacity: 0.4;
                                transform: scale(0.9);
                            }
                        }
                    `
                }} />
            </div>
        );
    };

    return (
        <>
            <Card style={{minHeight: '200px'}}>
                <CardBodyContentContainer>
                    <CardTitle icon={<BooksitoutIcon.password/>} title={`회원증 비밀번호`} subTitle={undefined} textSize={2} url={''}/>

                    <div style={{
                        padding: '20px',
                        textAlign: 'center',
                        minHeight: '60px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        {renderPasswordDisplay()}
                    </div>

                    {password && !isPasswordVisible && (
                        <div style={{
                            textAlign: 'center',
                            color: 'var(--text-secondary-color)',
                            fontSize: '0.9rem',
                            marginTop: '10px'
                        }}>
                            쓸어서 비밀번호를 확인하세요
                        </div>
                    )}

                    {password && isPasswordVisible && (
                        <div style={{
                            textAlign: 'center',
                            color: 'var(--text-secondary-color)',
                            fontSize: '0.9rem',
                            marginTop: '10px'
                        }}>
                            10초 후 다시 가려져요
                        </div>
                    )}

                </CardBodyContentContainer>
            </Card>

            <Modal
                isShowing={showPasswordModal}
                onClose={() => setShowPasswordModal(false)}
                titleText="회원증 비밀번호 설정"
                size="md"
                body={
                    <Form onSubmit={handlePasswordSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Control
                                type="text"
                                placeholder="회원증 비밀번호"
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                autoFocus
                                autoComplete="off"
                            />
                        </Form.Group>
                        <div className="d-flex gap-2">
                            <Button
                                variant="secondary"
                                onClick={() => setShowPasswordModal(false)}
                                className="flex-fill"
                            >
                                취소
                            </Button>
                            <Button
                                variant="book"
                                onClick={handlePasswordSubmit}
                                disabled={!newPassword.trim()}
                                className="flex-fill"
                            >
                                저장
                            </Button>
                        </div>
                    </Form>
                }
            />
        </>
    )
}
export default LibraryMembershipDetailPasswordCard
